// [CT_SEARCH_V2] 계약서 조건 검색 패널 (CT_SEARCH_V1 개정, 2026-10-05)
// V2: 검색어가 찾힌 항목·위치를 결과 행 아래에 미리보기로 보여주고 <mark> 로 강조
// ---------------------------------------------------------------------------
// [역할]  검색어·금액·날짜 범위를 입력받아 GET /api/contracts/search 호출 → 결과 표 출력
//         행 클릭 → 기존 상세화면 /contracts/:id (pages/ContractDetail.js)
// [연관]  pages/ContractList.js 가 이 패널을 맨 위에 놓고,
//         onActiveChange(true) 를 받으면 원래 "최근 100건" 표를 숨김
//         백엔드 routes/contracts.js 의 [CT_SEARCH_V1] 블록
// [상태 유지] 상세 보고 뒤로가기 해도 조건·결과가 남도록 sessionStorage 에 저장 (탭 닫으면 사라짐)
// [단위]  금액 입력은 "만원" (서버가 원으로 바꿔 비교), 결과 표시는 억/만 단위
// ---------------------------------------------------------------------------
import React, { useEffect, useState } from 'react';
import axios from 'axios';

const SS_KEY = 'ctSearchState_v2';   // V1 저장값(미리보기 없음)과 섞이지 않게 키 변경

// 검색 대상 항목 — key 는 백엔드 fields 값과 같아야 함
const FIELD_OPTS = [
  { key: 'party', label: '계약자' },
  { key: 'contact', label: '연락처' },
  { key: 'broker', label: '중개사무소' },
  { key: 'location', label: '소재지' },
  { key: 'clause', label: '특약내용' },
];

const EMPTY = {
  kw: '', fields: [], type: '',
  saleMin: '', saleMax: '', depMin: '', depMax: '', rentMin: '', rentMax: '',
  cFrom: '', cTo: '', mFrom: '', mTo: '', bFrom: '', bTo: '', eFrom: '', eTo: '',
};

// 천단위 콤마 (화면 표시용) / 콤마 제거 (저장용)
const comma = (v) => {
  const s = String(v || '').replace(/[^0-9]/g, '');
  return s ? Number(s).toLocaleString('ko-KR') : '';
};
const strip = (v) => String(v || '').replace(/[^0-9]/g, '');

// 원 → "1억 5,000만" 형태
const won = (v) => {
  if (!v) return '-';
  const m = Math.round(Number(v) / 10000);
  const eok = Math.floor(m / 10000);
  const rest = m % 10000;
  if (eok && rest) return `${eok}억 ${rest.toLocaleString('ko-KR')}만`;
  if (eok) return `${eok}억`;
  return `${rest.toLocaleString('ko-KR')}만`;
};

// 검색어 강조: 서버가 준 hl([[시작,끝],…]) 위치만 노란색 <mark> 로 칠함
//  (글자 비교를 화면에서 다시 하지 않음 → 하이픈 없이 친 전화번호도 정확히 강조)
function Hl({ text, hl }) {
  const parts = [];
  let i = 0;
  (hl || []).forEach(([a, b], k) => {
    if (a > i) parts.push(<span key={'t' + k}>{text.slice(i, a)}</span>);
    parts.push(<mark key={'m' + k} style={{ background: '#fde047', padding: 0 }}>{text.slice(a, b)}</mark>);
    i = b;
  });
  if (i < text.length) parts.push(<span key="end">{text.slice(i)}</span>);
  return <>{parts}</>;
}

const TYPE_LABEL = { SALE: '🏠 매매', RENT_TENANT: '🔑 임차', RENT_LANDLORD: '🏘️ 임대' };

const box = { border: '1px solid #ddd', borderRadius: 6, padding: 12, marginBottom: 12, background: '#fafafa' };
const row = { display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', marginBottom: 8 };
const lab = { width: 72, fontWeight: 'bold', fontSize: 13 };
const inp = { padding: '5px 7px', border: '1px solid #ccc', borderRadius: 4, fontSize: 13 };
const th = { padding: '8px', textAlign: 'left', fontSize: 13, whiteSpace: 'nowrap' };
const td = { padding: '8px', fontSize: 13, verticalAlign: 'top' };

export default function ContractSearchPanel({ onActiveChange }) {
  const [f, setF] = useState(EMPTY);          // 입력값
  const [list, setList] = useState(null);     // null = 검색 안 함 / [] = 결과 없음
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  // 처음 열릴 때: 저장된 조건·결과 복원 (상세 → 뒤로가기 대응)
  useEffect(() => {
    try {
      const saved = JSON.parse(sessionStorage.getItem(SS_KEY) || 'null');
      if (saved && saved.f) {
        setF({ ...EMPTY, ...saved.f });
        if (Array.isArray(saved.list)) { setList(saved.list); onActiveChange && onActiveChange(true); }
      }
    } catch (_) { /* 저장값이 깨졌으면 무시 */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const toggleField = (k) => setF((p) => ({
    ...p, fields: p.fields.includes(k) ? p.fields.filter((x) => x !== k) : [...p.fields, k],
  }));

  // 검색 실행 → 빈칸은 보내지 않음
  const search = async () => {
    setBusy(true); setErr('');
    try {
      const params = {};
      Object.keys(EMPTY).forEach((k) => {
        if (k === 'fields') return;
        if (f[k] !== '' && f[k] != null) params[k] = f[k];
      });
      params.fields = f.fields.length ? f.fields.join(',') : 'all';
      const r = await axios.get('/api/contracts/search', { params });
      const data = (r.data && r.data.data) || [];
      setList(data);
      onActiveChange && onActiveChange(true);
      sessionStorage.setItem(SS_KEY, JSON.stringify({ f, list: data }));
    } catch (e) {
      setErr('검색 실패: ' + ((e.response && e.response.data && e.response.data.message) || e.message));
    } finally {
      setBusy(false);
    }
  };

  // 초기화 → 원래 목록으로 돌아감
  const reset = () => {
    setF(EMPTY); setList(null); setErr('');
    sessionStorage.removeItem(SS_KEY);
    onActiveChange && onActiveChange(false);
  };

  // 금액 범위 입력 한 줄 (만원, 콤마 표시)
  const money = (label, a, b) => (
    <div style={row}>
      <span style={lab}>{label}</span>
      <input style={{ ...inp, width: 110, textAlign: 'right' }} inputMode="numeric" placeholder="최소"
        value={comma(f[a])} onChange={(e) => set(a, strip(e.target.value))} />
      <span>~</span>
      <input style={{ ...inp, width: 110, textAlign: 'right' }} inputMode="numeric" placeholder="최대"
        value={comma(f[b])} onChange={(e) => set(b, strip(e.target.value))} />
      <span style={{ fontSize: 12, color: '#888' }}>만원</span>
    </div>
  );

  // 날짜 범위 입력 한 줄
  const dates = (label, a, b) => (
    <div style={row}>
      <span style={lab}>{label}</span>
      <input type="date" style={inp} value={f[a]} onChange={(e) => set(a, e.target.value)} />
      <span>~</span>
      <input type="date" style={inp} value={f[b]} onChange={(e) => set(b, e.target.value)} />
    </div>
  );

  return (
    <div style={box}>
      {/* ---------- 검색어 ---------- */}
      <div style={row}>
        <span style={lab}>검색어</span>
        <input style={{ ...inp, flex: '1 1 220px' }} placeholder="이름·전화번호·주소·특약 단어 (띄어쓰기 = 모두 포함)"
          value={f.kw} onChange={(e) => set('kw', e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') search(); }} />
        <select style={inp} value={f.type} onChange={(e) => set('type', e.target.value)}>
          <option value="">전체 유형</option>
          <option value="SALE">매매</option>
          <option value="RENT">임대차</option>
        </select>
      </div>
      <div style={row}>
        <span style={lab}>찾을 곳</span>
        {FIELD_OPTS.map((o) => (
          <label key={o.key} style={{ fontSize: 13, cursor: 'pointer' }}>
            <input type="checkbox" checked={f.fields.includes(o.key)} onChange={() => toggleField(o.key)} /> {o.label}
          </label>
        ))}
        <span style={{ fontSize: 12, color: '#888' }}>(선택 없으면 전체 내용)</span>
      </div>

      {/* ---------- 금액 · 날짜 ---------- */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
        <div>
          {money('매매금액', 'saleMin', 'saleMax')}
          {money('보증금', 'depMin', 'depMax')}
          {money('월세', 'rentMin', 'rentMax')}
        </div>
        <div>
          {dates('계약일', 'cFrom', 'cTo')}
          {dates('중도금일', 'mFrom', 'mTo')}
          {dates('잔금일', 'bFrom', 'bTo')}
          {dates('만료일', 'eFrom', 'eTo')}
        </div>
      </div>

      <div style={{ ...row, marginTop: 4 }}>
        <button onClick={search} disabled={busy}
          style={{ background: '#1e40af', color: '#fff', border: 'none', padding: '8px 20px', borderRadius: 4, fontWeight: 'bold', cursor: 'pointer' }}>
          {busy ? '검색 중…' : '🔍 검색'}
        </button>
        <button onClick={reset}
          style={{ background: '#fff', border: '1px solid #ccc', padding: '8px 16px', borderRadius: 4, cursor: 'pointer' }}>
          초기화
        </button>
        {err && <span style={{ color: '#c00', fontSize: 13 }}>{err}</span>}
        {list && !err && <span style={{ fontSize: 13, color: '#555' }}>검색 결과 {list.length}건</span>}
      </div>

      {/* ---------- 결과 표 (검색했을 때만) ---------- */}
      {list && list.length > 0 && (
        <div style={{ overflowX: 'auto', marginTop: 8 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', background: '#fff' }}>
            <thead>
              <tr style={{ background: '#eef2ff', borderBottom: '2px solid #ccd' }}>
                <th style={th}>ID</th>
                <th style={th}>유형</th>
                <th style={th}>소재지</th>
                <th style={th}>계약자</th>
                <th style={th}>금액</th>
                <th style={th}>계약일</th>
                <th style={th}>중도금</th>
                <th style={th}>잔금</th>
                <th style={th}>만료</th>
                <th style={th}>등록일</th>{/* [CT_REGDATE_V1] */}
              </tr>
            </thead>
            <tbody>
              {list.map((c) => {
                const hasM = Array.isArray(c.matches) && c.matches.length > 0;
                const go = () => { window.location.href = `/contracts/${c.contract_id}`; };
                return (
                <React.Fragment key={c.contract_id}>
                <tr
                  style={{ borderBottom: hasM ? 'none' : '1px solid #eee', cursor: 'pointer' }}
                  onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f5f7ff'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#fff'; }}
                  onClick={go}>
                  <td style={td}>{c.contract_id}</td>
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>{TYPE_LABEL[c.contract_type] || c.contract_type}</td>
                  <td style={td}>{c.location_full_address || '-'}</td>
                  <td style={td}>
                    {[c.landlords, c.tenants].filter(Boolean).join(' / ') || '-'}
                    {c.brokers && <div style={{ fontSize: 11, color: '#888' }}>{c.brokers}</div>}
                  </td>
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>
                    {c.contract_type === 'SALE'
                      ? won(c.sale_price)
                      : `${won(c.deposit)}${c.monthly_rent ? ' / ' + won(c.monthly_rent) : ''}`}
                  </td>
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>{c.contract_date || '-'}</td>
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>{c.middle_date || '-'}</td>
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>{c.balance_date || '-'}</td>
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>{c.expiry_date || '-'}</td>
                  <td style={{ ...td, whiteSpace: 'nowrap', color: '#888' }}>{c.created_date || '-'}</td>
                </tr>
                {hasM && (
                  <tr style={{ borderBottom: '1px solid #eee', cursor: 'pointer', background: '#fffdf2' }} onClick={go}>
                    <td colSpan={10} style={{ padding: '4px 8px 8px 24px', fontSize: 12, color: '#444' }}>
                      {c.matches.map((m, k) => (
                        <div key={k} style={{ margin: '2px 0', lineHeight: 1.5 }}>
                          <span style={{ display: 'inline-block', minWidth: 64, marginRight: 6, padding: '0 6px', borderRadius: 3, background: '#e0e7ff', color: '#1e3a8a', fontWeight: 'bold', textAlign: 'center' }}>
                            {m.field}
                          </span>
                          <Hl text={m.text} hl={m.hl} />
                        </div>
                      ))}
                    </td>
                  </tr>
                )}
                </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {list && list.length === 0 && !err && (
        <div style={{ textAlign: 'center', padding: 20, color: '#999' }}>조건에 맞는 계약서가 없습니다.</div>
      )}
    </div>
  );
}
